import { useMemo } from "react";
import type { Message, Named, Uuid } from "@shared/api";
import { Markup } from "@shared/ui/Markup";
import { dayTime } from "@shared/lib/format";
import type { CustomEmoji } from "@shared/lib/emoji";
import type { MessageLink } from "@shared/lib/jump";
import { fromSelf, livePerson, Tile } from "@entities/user";
import { useMe } from "@entities/session";
import { useStore } from "@features/chat";
import { MessageExtras } from "./MessageRow";

/**
 * Сообщение целиком, но без ряда действий и реакций — для мест вне ленты, где
 * его показывают как есть, как в «Почте» Discord: лицо, имя, время, текст и
 * всё, что под ним (`MessageExtras`). Имена каналов и знаки берутся из того,
 * что стор уже знает о серверах: сообщение может быть с любого из них.
 */
export function MessagePreview({
  message,
  people,
  onOpenChannel,
  onOpenMessage,
}: {
  message: Message;
  people: Map<Uuid, Named>;
  onOpenChannel: (channelId: Uuid) => void;
  onOpenMessage?: (link: MessageLink) => void;
}) {
  const store = useStore();
  const self = useMe();
  const author = livePerson(message.author, people, fromSelf(self));

  const nameOf = useMemo(() => {
    const byHandle = new Map([...people.values()].map((person) => [person.handle, person]));
    return (handle: string) => {
      const person = byHandle.get(handle);
      return person ? person.display_name || person.username : null;
    };
  }, [people]);
  const channelOf = (id: string) => {
    for (const state of Object.values(store.states)) {
      const channel = state?.channels.find((item) => item.id === id);
      if (channel) return channel.name;
    }
    return null;
  };
  const emojiOf = (name: string): CustomEmoji | null => {
    for (const state of Object.values(store.states)) {
      const emoji = state?.emoji?.find((item) => item.name === name);
      if (emoji) return { ...emoji, server_name: state!.name };
    }
    return null;
  };

  return (
    <div className="msg-preview">
      <Tile person={author} size={40} mark={null} />
      <div className="msg-preview-body">
        <div className="msg-preview-head">
          <b className="ellipsis">{author.name}</b>
          <span className="msg-preview-at">{dayTime(message.created)}</span>
        </div>
        {message.content && (
          <div className="msg-preview-text">
            <Markup
              text={message.content}
              nameOf={nameOf}
              mine={self.handle}
              emojiOf={emojiOf}
              channelOf={channelOf}
              onOpenChannel={onOpenChannel}
              onOpenMessage={onOpenMessage}
            />
          </div>
        )}
        <MessageExtras
          message={message}
          people={people}
          nameOf={nameOf}
          mine={self.handle}
          emojiOf={emojiOf}
          channelOf={channelOf}
          roleOf={() => null}
          onOpenChannel={onOpenChannel}
          onOpenMessage={onOpenMessage}
        />
      </div>
    </div>
  );
}
